import { useState, useEffect, useCallback } from "react";
import { api } from "@/utils/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Sparkles,
  Plus,
  Trash2,
  Copy,
  Check,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  KeyRound,
} from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * "Connect AI Assistant" — lets a user generate read-only Personal Access
 * Tokens so their ChatGPT (Custom GPT Action) can read their travel plans.
 * Fully optional. Tokens are revocable and shown in plaintext only once.
 */
const AssistantConnectSection = () => {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(false);
  const [label, setLabel] = useState("My ChatGPT");
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState(null); // plaintext, shown once
  const [copied, setCopied] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);

  const schemaUrl = `${BACKEND}/api/assistant/openapi.json`;
  const mcpUrl = `${BACKEND}/api/mcp`;

  const fetchTokens = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/assistant/tokens`);
      setTokens(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const handleCreate = async () => {
    if (!label.trim()) {
      toast.error("Give your token a name first");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post(`/assistant/tokens`, { label: label.trim() });
      setNewToken(res.data.token);
      setCopied(false);
      setLabel("My ChatGPT");
      setShowInstructions(true);
      fetchTokens();
      toast.success("Token created — copy it now, it won't be shown again");
    } catch (e) {
      toast.error("Failed to create token");
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id) => {
    if (!window.confirm("Revoke this token? Any assistant using it will lose access immediately.")) return;
    try {
      await api.delete(`/assistant/tokens/${id}`);
      toast.success("Token revoked");
      fetchTokens();
    } catch (e) {
      toast.error("Failed to revoke token");
    }
  };

  const copy = async (text, which) => {
    try {
      await navigator.clipboard.writeText(text);
      if (which === "token") setCopied(true);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "never";
    try {
      return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return iso;
    }
  };

  return (
    <div data-testid="assistant-connect-section">
      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="h-5 w-5 text-indigo-500" />
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Connect AI Assistant</h3>
        <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">Optional</Badge>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        Let your own ChatGPT read your trips so it can help plan, critique locations, and answer questions about your itinerary. Works with ChatGPT Plus and up (and other MCP assistants like Claude).
      </p>

      <div className="flex items-start gap-2 text-xs text-emerald-700 dark:text-emerald-400 mb-4">
        <ShieldCheck className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span className="min-w-0">Read-only. Your assistant can view your plans but never change them. Revoke access anytime.</span>
      </div>

      {/* Create token */}
      <div className="flex items-end gap-2 mb-4">
        <div className="flex-1 min-w-0">
          <Label htmlFor="assistant-token-label" className="text-xs">Token name</Label>
          <Input
            id="assistant-token-label"
            data-testid="assistant-token-label-input"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="My ChatGPT"
            maxLength={100}
          />
        </div>
        <Button
          onClick={handleCreate}
          disabled={creating}
          data-testid="assistant-generate-token-btn"
          className="bg-indigo-500 hover:bg-indigo-600 flex-shrink-0"
        >
          <Plus className="h-4 w-4 mr-1" />
          Generate
        </Button>
      </div>

      {/* One-time plaintext reveal */}
      {newToken && (
        <div
          className="rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 mb-4"
          data-testid="assistant-new-token-reveal"
        >
          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-300 text-sm font-semibold mb-2">
            <KeyRound className="h-4 w-4" />
            Copy this token now — it won't be shown again.
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all text-xs bg-white dark:bg-gray-900 rounded px-2 py-1.5 border border-amber-200 dark:border-amber-800">
              {newToken}
            </code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copy(newToken, "token")}
              data-testid="assistant-copy-token-btn"
            >
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
          <button
            className="text-xs text-gray-500 mt-2 hover:underline"
            onClick={() => setNewToken(null)}
          >
            Done, hide it
          </button>
        </div>
      )}

      {/* Existing tokens */}
      {tokens.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="assistant-token-list">
          {tokens.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2"
              data-testid={`assistant-token-row-${t.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{t.label}</p>
                <p className="text-xs text-gray-500">
                  {t.token_prefix}••••  ·  created {fmtDate(t.created_at)}  ·  last used {fmtDate(t.last_used_at)}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleRevoke(t.id)}
                className="h-8 w-8 text-red-500 hover:text-red-700"
                data-testid={`assistant-revoke-token-${t.id}`}
                title="Revoke"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {!loading && tokens.length === 0 && !newToken && (
        <p className="text-xs text-gray-400 mb-4">No tokens yet. Generate one to connect an assistant.</p>
      )}

      {/* Setup instructions */}
      <button
        className="flex items-center gap-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        onClick={() => setShowInstructions((v) => !v)}
        data-testid="assistant-instructions-toggle"
      >
        {showInstructions ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        How to connect it to ChatGPT
      </button>
      {showInstructions && (
        <div className="mt-2 rounded-lg bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 p-3 text-sm text-gray-700 dark:text-gray-300 space-y-3">
          <div>
            <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              ChatGPT (Plus, Pro, Business) — via a Connector
            </p>
            <p className="text-xs text-gray-500 mb-2">
              Do this on the ChatGPT <strong>website</strong> (not the phone app).
            </p>
            <ol className="list-decimal list-inside space-y-1.5">
              <li>Generate a token above and copy it (you'll paste it once during sign-in).</li>
              <li>In ChatGPT: <strong>Settings → Apps &amp; Connectors → Advanced</strong> and turn on <strong>Developer mode</strong>.</li>
              <li>Go to <strong>Apps &amp; Connectors → Create</strong>. Give it a name like “Travel Gadjet”.</li>
              <li>
                Set the <strong>MCP Server URL</strong> to:
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 break-all text-xs bg-white dark:bg-gray-900 rounded px-2 py-1 border border-gray-200 dark:border-gray-700">
                    {mcpUrl}
                  </code>
                  <Button size="sm" variant="outline" onClick={() => copy(mcpUrl, "mcp")} data-testid="assistant-copy-mcp-url-btn">
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </li>
              <li>For <strong>Authentication</strong>, choose <strong>OAuth</strong>. Leave the advanced fields alone — ChatGPT fills the Auth / Token / Registration URLs automatically by reading them from the server.</li>
              <li>Click <strong>Create</strong>, then enable Travel Gadjet in a chat. The first time you use it, ChatGPT opens a <strong>Travel Gadjet authorize page</strong> — paste the token you generated above and tap <em>Authorize</em>.</li>
              <li>Now ask: <em>“What's my next trip and who's coming?”</em></li>
            </ol>
            <p className="text-xs text-gray-500 mt-2">
              Why a token instead of a normal login? It keeps sign-in simple and reliable inside ChatGPT's browser. ChatGPT stays connected afterwards and refreshes access on its own — you only paste the token once.
            </p>
          </div>

          <div className="pt-1 border-t border-gray-200 dark:border-gray-800">
            <p className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Business / Enterprise / Edu — via a Custom GPT (optional)
            </p>
            <p className="text-xs text-gray-500 mb-1">
              Only if your workspace still allows building GPTs. Create a GPT → Actions → Import from URL:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 break-all text-xs bg-white dark:bg-gray-900 rounded px-2 py-1 border border-gray-200 dark:border-gray-700">
                {schemaUrl}
              </code>
              <Button size="sm" variant="outline" onClick={() => copy(schemaUrl, "url")} data-testid="assistant-copy-schema-url-btn">
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <p className="text-xs text-gray-500 pt-1">
            Keep the connection private — your token grants read access to your trips. Revoke it anytime above.
          </p>
        </div>
      )}
    </div>
  );
};

export default AssistantConnectSection;
